import type { ComponentProps, ComponentType, FunctionComponent } from 'react'

import { useFocusRing } from '@react-aria/focus'
import type { IStyledComponent } from 'styled-components'

import type { FocusVisibleProps } from '.'

/**
 * Helper to inject `isFocusVisible` prop into a styled-component.
 */
export const withFocusVisible: {
	<T extends IStyledComponent<'web', any>>(Component: T): FunctionComponent<Omit<ComponentProps<T>, 'isFocusVisible'>>
	<T extends FocusVisibleProps>(Component: ComponentType<T>): FunctionComponent<Omit<T, 'isFocusVisible'>>
} = <T extends FocusVisibleProps>(Component: ComponentType<T>): FunctionComponent<Omit<T, 'isFocusVisible'>> => {
	const WrappedComponent = (props: Omit<T, 'isFocusVisible'>) => {
		const { isFocusVisible, focusProps } = useFocusRing()
		// @ts-expect-error The permissive typing from useFocusRing makes typing
		// difficult. Rather than fighting, let's just disable this check.
		return <Component {...props} {...focusProps} isFocusVisible={isFocusVisible} />
	}

	WrappedComponent.displayName = 'withFocusVisible'

	return WrappedComponent
}
